import {
  CHILD_PROFILES_CLAIM,
  type ChildProfilesClaim,
  ORGANIZATION_CLAIM,
  type OrganizationClaim,
  OTHER_PROFILES_CLAIM,
  type OtherProfilesClaim,
  PROFILE_CLAIM,
  type ProfileClaim,
  ROLES_CLAIM,
  type RolesClaim,
} from '@bp/pim-auth-constants';
import dayjs from 'dayjs';
import type { User } from 'oidc-client-ts';
import { bpUserManager } from './userManager';

type ExtendedUser = Omit<User, 'profile'> & {
  profile: User['profile'] & UserInfoResponse;
};

const GetAuthData = async (): Promise<ExtendedUser | null> => {
  try {
    const userManager = bpUserManager.getUserManager();
    return (await userManager.getUser()) as ExtendedUser | null;
  } catch (_e: unknown) {
    //console.error(e);
    return null;
  }
};

const SetData = async (data: ExtendedUser | null) => {
  await bpUserManager.getUserManager().storeUser(data);
};

const RemoveData = async () => {
  await bpUserManager.getUserManager().removeUser();
};

export const clearTokens = async () => {
  await RemoveData();
};

export const getClaims = async (): Promise<UserInfoResponse | null> => {
  const data = await GetAuthData();
  if (data) {
    return data.profile;
  }
  return null;
};

export const getTokens = async (): Promise<{
  access_token?: string;
  refresh_token?: string;
  expires_at?: number;
  id_token?: string;
} | null> => {
  const TokenData = await GetAuthData();
  if (TokenData) {
    const accessToken = TokenData.access_token;
    const refreshToken = TokenData.refresh_token;
    let expiresAt = TokenData.expires_at;
    const idToken = TokenData.id_token;

    if (expiresAt && expiresAt <= dayjs().unix()) {
      expiresAt = dayjs().add(expiresAt).unix();
      await SetData({ ...TokenData, expires_at: expiresAt });
    }

    if (accessToken) {
      return {
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_at: expiresAt,
        id_token: idToken,
      };
    }
  }
  return null;
};

export type UserInfoResponse = {
  sub: string;
  PROFILE_CLAIM: ProfileClaim;
  OTHER_PROFILES_CLAIM: OtherProfilesClaim;
  CHILD_PROFILES_CLAIM: ChildProfilesClaim;
  ORGANIZATION_CLAIM: OrganizationClaim;
  ROLES_CLAIM: RolesClaim;
};

export async function updateClaims(callback?: (userInfo: UserInfoResponse | undefined) => void) {
  let userInfo: UserInfoResponse | undefined;
  const tokens = await getTokens();

  if (tokens?.access_token !== null) {
    userInfo = await fetch(import.meta.env.VITE_APP_AUTH_SERVER_URI + '/me', {
      headers: { Authorization: 'Bearer ' + tokens?.access_token },
    })
      .then((r) => r.json() as unknown as UserInfoResponse)
      .catch(() => undefined);

    if (userInfo) {
      const newUserInfo = await GetAuthData();
      if (newUserInfo?.profile.sub === userInfo.sub) {
        newUserInfo.profile[PROFILE_CLAIM] = userInfo[PROFILE_CLAIM as keyof UserInfoResponse];
        newUserInfo.profile[OTHER_PROFILES_CLAIM] = userInfo[OTHER_PROFILES_CLAIM as keyof UserInfoResponse];
        newUserInfo.profile[CHILD_PROFILES_CLAIM] = userInfo[CHILD_PROFILES_CLAIM as keyof UserInfoResponse];
        newUserInfo.profile[ORGANIZATION_CLAIM] = userInfo[ORGANIZATION_CLAIM as keyof UserInfoResponse];
        newUserInfo.profile[ROLES_CLAIM] = userInfo[ROLES_CLAIM as keyof UserInfoResponse];
      }

      await SetData(newUserInfo);
    } else {
      await RemoveData();
    }
  }

  callback && callback(userInfo);
  return userInfo;
}
